import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { detailsShellClass, detailsSummaryClass, detailsTitleClass } from '@/components/ui/cardStyles';
import { labelClass } from '@/components/ui/inputStyles';

type Props = {
  areaId: number;
};

export function MicroPromotionSensitiveWordsPanel({ areaId }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [words, setWords] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; sensitive_words?: string }>('get_micro_promotion_sensitive_words', {
        method: 'GET',
        query: { area_id: areaId },
      });
      if (r.ok) setWords(r.sensitive_words ?? '');
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    setSaving(true);
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_micro_promotion_sensitive_words', {
        method: 'POST',
        body: JSON.stringify({ area_id: areaId, sensitive_words: words }),
      });
      setMsg(r.ok ? '已保存敏感词' : '保存失败');
      if (r.ok) await load();
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className={detailsShellClass} open>
      <summary className={detailsSummaryClass}>
        <h2 className={detailsTitleClass}>敏感词管理（图文 · 招聘共用）</h2>
      </summary>
      <div className="space-y-4 p-5 text-sm text-slate-700">
        <p className="text-slate-600">
          本区域下<strong className="font-medium text-slate-800">图文信息</strong>与
          <strong className="font-medium text-slate-800">招聘信息</strong>
          共用同一套敏感词。入库或编辑时若命中，条目将进入<strong className="font-medium text-slate-800">待审核</strong>
          ，不会进入 Android 推广队列，直至管理员或站长审核通过。
        </p>
        {loading ? (
          <Spinner label="加载敏感词" />
        ) : (
          <>
            <label className="block">
              <span className={labelClass}>敏感词列表</span>
              <textarea
                className="mt-1.5 w-full min-h-[120px] rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm text-slate-800 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                value={words}
                onChange={(e) => setWords(e.target.value)}
                placeholder={'一行一个词，例如：\n违禁词\n广告代理'}
                spellCheck={false}
              />
              <span className="mt-1.5 block text-xs text-slate-500">匹配时不区分大小写；留空表示不做敏感词拦截。</span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="primary" disabled={saving} onClick={() => void save()}>
                {saving ? '保存中…' : '保存敏感词'}
              </Button>
              <Button type="button" variant="secondary" disabled={loading} onClick={() => void load()}>
                重新加载
              </Button>
              {msg ? (
                <span className={`text-xs ${msg.includes('失败') ? 'text-rose-600' : 'text-emerald-700'}`}>{msg}</span>
              ) : null}
            </div>
          </>
        )}
      </div>
    </details>
  );
}
